import { useState } from "react";
import type { VehicleImage } from "@/types";

const FALLBACK =
  "https://readdy.ai/api/search-image?query=Studio%20automotive%20photography%20of%20a%20sleek%20silhouette%20car%20on%20a%20seamless%20charcoal%20grey%20background%20with%20soft%20reflective%20floor%20dramatic%20softbox%20lighting%20premium%20editorial%20advertisement%20style&width=1200&height=800&seq=gari-placeholder-01&orientation=landscape";

interface GalleryProps {
  images: VehicleImage[] | null;
  title: string;
}

export default function Gallery({ images, title }: GalleryProps) {
  const list = images && images.length ? images : [{ url: FALLBACK, type: "image" }];
  const [active, setActive] = useState(0);
  const [lightbox, setLightbox] = useState(false);

  const go = (dir: number) => {
    setActive((prev) => (prev + dir + list.length) % list.length);
  };

  return (
    <div>
      <div className="relative h-[280px] w-full overflow-hidden rounded-2xl border border-background-200/70 bg-background-100 md:h-[460px]">
        <img
          src={list[active].url}
          alt={`${title} — photo ${active + 1}`}
          title={`${title} rental in Kenya — Gari`}
          className="h-full w-full cursor-zoom-in object-cover object-top"
          onClick={() => setLightbox(true)}
        />
        <div className="absolute right-3 top-3 flex items-center gap-2">
          <span className="inline-flex items-center gap-1 rounded-full bg-background-50/80 px-3 py-1.5 text-xs font-medium text-foreground-950 backdrop-blur">
            <i className="ri-image-2-line"></i> {active + 1} / {list.length}
          </span>
          <button
            type="button"
            onClick={() => setLightbox(true)}
            aria-label="View full screen"
            className="flex h-9 w-9 items-center justify-center rounded-full bg-background-50/80 text-lg text-foreground-950 backdrop-blur transition-colors hover:bg-background-50"
          >
            <i className="ri-expand-diagonal-line"></i>
          </button>
        </div>
      </div>

      {list.length > 1 && (
        <div className="mt-3 flex gap-3 overflow-x-auto pb-1 no-scrollbar">
          {list.map((image, index) => (
            <button
              key={index}
              type="button"
              onClick={() => setActive(index)}
              aria-label={`Show photo ${index + 1}`}
              className={`relative h-16 w-24 shrink-0 overflow-hidden rounded-lg border-2 transition-colors md:h-20 md:w-28 ${
                active === index ? "border-primary-500" : "border-transparent opacity-70 hover:opacity-100"
              }`}
            >
              <img src={image.url} alt="" className="h-full w-full object-cover object-top" />
            </button>
          ))}
        </div>
      )}

      {lightbox && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/90 p-4">
          <button
            type="button"
            onClick={() => setLightbox(false)}
            aria-label="Close"
            className="absolute right-5 top-5 flex h-11 w-11 items-center justify-center rounded-full bg-background-50/15 text-2xl text-white backdrop-blur"
          >
            <i className="ri-close-line"></i>
          </button>
          <button
            type="button"
            onClick={() => go(-1)}
            aria-label="Previous"
            className="absolute left-3 flex h-11 w-11 items-center justify-center rounded-full bg-background-50/15 text-2xl text-white backdrop-blur md:left-8"
          >
            <i className="ri-arrow-left-s-line"></i>
          </button>
          <img
            src={list[active].url}
            alt={`${title} full view`}
            className="max-h-[85vh] w-auto max-w-[92vw] rounded-xl object-contain"
          />
          <button
            type="button"
            onClick={() => go(1)}
            aria-label="Next"
            className="absolute right-3 flex h-11 w-11 items-center justify-center rounded-full bg-background-50/15 text-2xl text-white backdrop-blur md:right-8"
          >
            <i className="ri-arrow-right-s-line"></i>
          </button>
        </div>
      )}
    </div>
  );
}